import { getUserIdFromJid } from '../../Helpers/Utility';
import { toMillis } from '../poll/pollRules';
import { isLiveLocation } from '../recentChat/previewText';

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

/**
 * meta_data of a message as an object. The SDK parses it, but older paths keep the string.
 *
 * @param {object|string} metaData Message meta_data.
 * @returns {object} Parsed meta_data, empty when missing or unreadable.
 */
export const readMetaData = metaData => {
    if (metaData && typeof metaData === 'object') return metaData;
    if (typeof metaData !== 'string' || !metaData) return {};
    try {
        return JSON.parse(metaData) || {};
    } catch {
        return {};
    }
};

/**
 * Whether a location reads as live. Every place that labels a share live asks here,
 * so the card, the previews and the lists agree.
 *
 * @param {object} msgBody Message body holding the location.
 * @param {object|string} metaData Message meta_data, parsed or not.
 * @returns {boolean} True for a live share.
 */
export const isLiveLocationLabel = (msgBody, metaData) => isLiveLocation(msgBody, readMetaData(metaData));

/**
 * Whether a location message is a live share the web follows.
 *
 * @param {object} message Location message.
 * @returns {boolean} True for a live share.
 */
export const isLiveLocationMessage = message => isLiveLocationLabel(message?.msgBody, message?.metaData);

/**
 * End of the share as sent with the message, used until the server copy is loaded.
 *
 * @param {object} message Location message.
 * @returns {number} Epoch milliseconds, 0 when unknown.
 */
export const getMessageLiveEndsAt = message => toMillis(readMetaData(message?.metaData).liveEndsAt);

/**
 * A position the map can draw; coordinates may arrive as strings.
 *
 * @param {object} position Object holding latitude and longitude.
 * @returns {{lat: number, lng: number}|null} Map position, or null when unusable.
 */
export const toLatLng = position => {
    if (!position) return null;
    const lat = Number(position.latitude);
    const lng = Number(position.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > MAX_LATITUDE || Math.abs(lng) > MAX_LONGITUDE) return null;
    return { lat, lng };
};

/**
 * Whether two JIDs, or a JID and an id, name the same chat.
 *
 * @param {string} first JID or id.
 * @param {string} second JID or id.
 * @returns {boolean} True for the same chat.
 */
export const isSameChat = (first, second) =>
    Boolean(first && second) && getUserIdFromJid(String(first)) === getUserIdFromJid(String(second));
